import { useState, useCallback } from 'react';
import { TFunction } from 'i18next';
import {
  Documentation,
  useEdmDocumentationBulkDestroyMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { AnalyticsDocumentRow } from './utils/getTopDocumentsTableColumns';

interface UseTopDocumentsActionsArgs {
  t: TFunction<'translation', undefined>;
  onDeleted: () => void;
}

export interface TopDocumentsActionsResult {
  fileViewerDocument: Documentation | null;
  versionNotesDocument: Documentation | null;
  deleteIds: string[];
  onOpenFile: (item: AnalyticsDocumentRow) => void;
  onViewDocument: (item: Documentation) => void;
  onVersionNotes: (item: Documentation) => void;
  onDelete: (item: Documentation) => void;
  onConfirmedDelete: () => void;
  closeDeleteModal: () => void;
  closeFileViewer: () => void;
  closeVersionNotes: () => void;
}

/**
 * Owns the modal/dialog state for the Top Documents table:
 *   - file viewer (preview attachment)
 *   - version notes
 *   - bulk delete confirmation + mutation
 *
 * `onDeleted` is called after a successful destroy so the caller can refetch
 * the underlying analytics list.
 */
export default function useTopDocumentsActions({
  t,
  onDeleted,
}: UseTopDocumentsActionsArgs): TopDocumentsActionsResult {
  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[]>([]);

  const onOpenFile = useCallback((item: AnalyticsDocumentRow) => {
    if (item.documentation) setFileViewerDocument(item.documentation);
  }, []);

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const onDelete = useCallback((item: Documentation) => {
    setDeleteIds([item.id]);
  }, []);

  const [destroyMutation] = useEdmDocumentationBulkDestroyMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });

  const onConfirmedDelete = useCallback(() => {
    if (!deleteIds.length) return;
    destroyMutation({
      variables: { ids: deleteIds },
      onCompleted: () => {
        setDeleteIds([]);
        toast({ content: t('destroy-documents-success') });
        onDeleted();
      },
    });
  }, [deleteIds, destroyMutation, onDeleted, t]);

  const closeDeleteModal = useCallback(() => setDeleteIds([]), []);
  const closeFileViewer = useCallback(() => setFileViewerDocument(null), []);
  const closeVersionNotes = useCallback(
    () => setVersionNotesDocument(null),
    [],
  );

  return {
    fileViewerDocument,
    versionNotesDocument,
    deleteIds,
    onOpenFile,
    onViewDocument,
    onVersionNotes,
    onDelete,
    onConfirmedDelete,
    closeDeleteModal,
    closeFileViewer,
    closeVersionNotes,
  };
}
